import { useEffect, useState } from 'react';
import { Search, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';

const roleLabel = (r) => r === 'corporate_hr' ? 'CORPORATE' : r === 'vessel_hr' ? 'VESSEL' : (r || 'unknown').toUpperCase();
const actionLabel = (a) => (a || '').replaceAll('_', ' ');

export default function AuditLog({ user }) {
  const [data, setData] = useState(null);
  const [role, setRole] = useState('');
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    const params = new URLSearchParams();
    if (role) params.set('role', role);
    if (entity) params.set('entity_type', entity);
    if (action) params.set('action', action);
    if (q) params.set('q', q);
    api(`/audit?${params.toString()}`).then(setData);
  }, [role, entity, action, q]);

  if (user.role !== 'corporate_hr') {
    return <p className="muted" data-testid="audit-forbidden">Corporate HR access required.</p>;
  }

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow">GOVERNANCE</span>
          <h1>Audit workspace</h1>
          <p className="muted">Every mutation across the fleet — filter by role, entity or action.</p>
        </div>
      </div>
      <div className="toolbar">
        <div className="search-box">
          <Search size={17} />
          <input data-testid="audit-search-input" placeholder="Search actor, case id or action" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select data-testid="audit-role-filter" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          <option value="corporate_hr">Corporate HR</option>
          <option value="vessel_hr">Vessel HR</option>
        </select>
        <select data-testid="audit-entity-filter" value={entity} onChange={(e) => setEntity(e.target.value)}>
          <option value="">All entities</option>
          {data?.by_entity?.map((b) => <option key={b.label} value={b.label}>{b.label} ({b.value})</option>)}
        </select>
        <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="">All actions</option>
          {data?.by_action?.map((b) => <option key={b.label} value={b.label}>{actionLabel(b.label)} ({b.value})</option>)}
        </select>
      </div>
      <section className="panel">
        <div className="panel-title">
          <div>
            <span className="eyebrow">EVENTS</span>
            <h2><ShieldCheck size={16} style={{ display: 'inline', marginRight: 6 }} /> {data?.shown ?? '…'} of {data?.total ?? '…'} events</h2>
          </div>
        </div>
        <div className="admin-list" style={{ marginTop: 14 }}>
          {(!data || data.entries.length === 0) && <p className="muted">No audit events match the current filters.</p>}
          {data?.entries?.map((e) => (
            <div className="admin-row" key={e.id} data-testid={`audit-entry-${e.id}`}>
              <div>
                <span className={`role-badge ${e.actor_role || 'unknown'}`}>{roleLabel(e.actor_role)}</span>
                <strong>{actionLabel(e.action)}</strong>
                <small className="muted"> · {e.entity_type} · {e.entity_id}</small>
                {e.details && Object.keys(e.details).length > 0 && (
                  <div className="mono muted" style={{ marginTop: 6, fontSize: 11 }}>{JSON.stringify(e.details)}</div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <strong>{e.actor_name}</strong>
                <small className="muted" style={{ display: 'block' }}>{new Date(e.timestamp).toLocaleString()}</small>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
